import { useEffect, useRef, useState, useCallback } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTheme, useAuth, usePlatformPublicSettings } from '../hooks'
import { STRINGS } from '../constants/strings'
import { createPortal } from 'react-dom'
import PageRangePicker from './pageRangePicker'
import BuyCreditsModal from './buyCreditsModal'
import { api } from '../data/api'

const S = STRINGS.admin

// ── Constants ─────────────────────────────────────────────────────────────────

export const NAME_MAX = 150
export const DESC_MAX = 200

export const Q_TYPES = [
  { key: 'mcq',          label: 'MCQ',           desc: 'Multiple Choice Questions (only one option is selectable)' },
  { key: 'msq',          label: 'MSQ',           desc: 'Multiple Selection Questions (can select multiple options)' },
  { key: 'true_false',   label: 'True / False',  desc: 'True or false'    },
  { key: 'short_answer', label: 'Short Answer',  desc: 'Written response' },
]

// Gated features unlock purely by the org's current creditsBalance being at
// or above that specific feature's price in PlatformSettings.featureUnlockPrices
// (exposed read-only via /api/platform-settings/public) — a price of 0 (or
// missing) means always unlocked, same as MCQ/True-False today. This is the
// ONLY unlock mechanism — the old per-org manual examPermissions grant has
// been disabled.
export function resolveExamAccess(user, featureUnlockPrices) {
  const isSuperadmin = user?.role === 'superadmin'
  const balance = user?.creditsBalance ?? 0
  const prices = featureUnlockPrices ?? {}
  const hasAccess = key => isSuperadmin || (prices[key] ?? 0) <= balance

  return {
    lockedQTypeKeys: isSuperadmin ? [] : Q_TYPES.filter(t => !hasAccess(t.key)).map(t => t.key),
    bloomsAllowed: hasAccess('bloomsTaxonomy'),
  }
}

export const DIFFICULTIES = [
  { key: 'Beginner',     label: 'Beginner'     },
  { key: 'Intermediate', label: 'Intermediate' },
  { key: 'Advanced',     label: 'Advanced'     },
  { key: 'Expert',       label: 'Expert'       },
]

export const DEFAULT_Q_SETTINGS = {
  mcq:          { enabled: true, count: 40, timePerQ: 1 },
  true_false:   { enabled: true, count: 20, timePerQ: 1 },
  short_answer: { enabled: true, count: 20, timePerQ: 1 },
  msq:          { enabled: true, count: 20, timePerQ: 1 },
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtSize(bytes) {
  return bytes > 1_048_576
    ? `${(bytes / 1_048_576).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`
}

export function totalQFor(qSettings) {
  return Q_TYPES.filter(t => qSettings[t.key]?.enabled).reduce((s, t) => s + (qSettings[t.key]?.count ?? 0), 0)
}

// Drops files with no ranges set (they just use the whole document) and returns
// undefined entirely if nothing's configured — matches the AI service's
// "omit it entirely and nothing changes" backward-compat contract.
export function cleanPageRanges(pageRanges) {
  const entries = Object.entries(pageRanges ?? {}).filter(([, ranges]) => ranges?.length > 0)
  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

// Status badge shown per textbook file — opens the page-range picker, or shows
// how many ranges are already set. No support pre-check — the AI service's own
// generation-time 400 (with its real reason) is the source of truth for whether
// a given PDF's printed page numbering can be detected.
export function PageRangeBadge({ name, pageRanges, onClick }) {
  const count = pageRanges?.[name]?.length ?? 0
  return (
    <button type="button" onClick={onClick}
      className="text-[0.65rem] font-medium px-2 py-0.5 rounded-full flex-shrink-0 transition-all"
      style={count > 0
        ? { background: 'color-mix(in srgb, var(--brand) 15%, transparent)', color: 'var(--brand)' }
        : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
      {count > 0 ? `${count} range${count > 1 ? 's' : ''}` : 'Set page range'}
    </button>
  )
}

// Hard ceilings the AI service enforces per generation call — must match the AI
// service's own constants. The exam pool and each practice difficulty pool are
// separate calls with separate ceilings (practice raised to 450 to give the
// multiplier real headroom; exam pool stays at 100).
export const MAX_EXAM_QUESTIONS     = 100
export const MAX_PRACTICE_QUESTIONS = 450
export const PRACTICE_MULTIPLIER_MAX = 10 // matches the Exam model's practiceMultiplier max

export const PRACTICE_MULTIPLIER_STEP = 0.5

export function maxPracticeMultiplier(totalQ) {
  if (totalQ <= 0) return PRACTICE_MULTIPLIER_MAX
  // Snap down to the nearest 0.5 so the shown max is always itself pickable.
  const steppedMax = Math.floor((MAX_PRACTICE_QUESTIONS / totalQ) / PRACTICE_MULTIPLIER_STEP) * PRACTICE_MULTIPLIER_STEP
  return Math.max(1, Math.min(PRACTICE_MULTIPLIER_MAX, steppedMax))
}

// Only short-answer questions can be Create-level — 0 if short_answer is disabled,
// regardless of any stale count value left over from before it was toggled off.
export function saCountFor(qSettings) {
  return qSettings.short_answer?.enabled ? (qSettings.short_answer?.count ?? 0) : 0
}

function fmtDuration(mins) {
  if (mins < 60) return `${Math.round(mins)} min`
  const h = Math.floor(mins / 60), m = Math.round(mins % 60)
  return m > 0 ? `${h}h ${m}min` : `${h}h`
}

// ── Sub-components ────────────────────────────────────────────────────────────

export function CharCount({ value, max }) {
  const left = max - value.length
  return (
    <span className="text-[0.65rem] tabular-nums" style={{ color: left <= 10 ? '#f87171' : 'var(--text-3)' }}>
      {left}
    </span>
  )
}

export function FileDropZone({ files, setFiles, dragging, setDragging, fileRef, maxMB = 15, pageRanges, onSetPageRanges }) {
  const MAX_TOTAL = maxMB * 1024 * 1024
  const totalSize  = files.reduce((s, f) => s + f.size, 0)
  const limitReached = totalSize >= MAX_TOTAL
  const [warning, setWarning] = useState(null)
  const warningTimer = useRef(null)
  useEffect(() => () => clearTimeout(warningTimer.current), [])

  function addFiles(fileList) {
    const pdfs = Array.from(fileList).filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'))
    if (!pdfs.length) return
    if (limitReached) return

    const toAdd = []
    let running = totalSize
    for (const f of pdfs) {
      if (running + f.size <= MAX_TOTAL) { toAdd.push(f); running += f.size }
    }
    if (toAdd.length < pdfs.length) {
      clearTimeout(warningTimer.current)
      setWarning(`Total size limit of ${maxMB} MB reached — some files were not added.`)
      warningTimer.current = setTimeout(() => setWarning(null), 5000)
    }
    if (!toAdd.length) return
    setFiles(prev => { const ex = new Set(prev.map(f => f.name)); return [...prev, ...toAdd.filter(f => !ex.has(f.name))] })
  }
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
          <div className="h-full rounded-full transition-all duration-300"
               style={{ width: `${Math.min(100, (totalSize / MAX_TOTAL) * 100)}%`,
                        background: limitReached ? '#f87171' : totalSize / MAX_TOTAL > 0.8 ? '#fbbf24' : 'var(--brand)' }} />
        </div>
        <span className="text-xs flex-shrink-0" style={{ color: limitReached ? '#f87171' : 'var(--text-3)' }}>
          {fmtSize(totalSize)} / {maxMB} MB
        </span>
      </div>
      {warning && (
        <div className="mb-2 flex items-center gap-2 px-3 py-2 rounded-xl text-xs animate-fade-in"
             style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', color: '#f87171' }}>
          <span>⚠</span>
          <span className="flex-1">{warning}</span>
          <button type="button" onClick={() => { clearTimeout(warningTimer.current); setWarning(null) }}
            className="flex-shrink-0 opacity-70 hover:opacity-100">✗</button>
        </div>
      )}
      {limitReached ? (
        <div className="flex flex-col items-center justify-center h-16 rounded-xl border-2 border-dashed"
             style={{ borderColor: 'rgba(248,113,113,0.4)', background: 'rgba(248,113,113,0.05)' }}>
          <span className="text-xs font-medium" style={{ color: '#f87171' }}>Size limit reached ({maxMB} MB)</span>
          <span className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>Remove files to add more</span>
        </div>
      ) : (
        <label
          className={`flex flex-col items-center justify-center h-20 rounded-xl border-2 border-dashed transition-all cursor-pointer ${
            dragging ? 'border-[var(--brand)] bg-[var(--brand)]/10' : 'border-[var(--border-2)] bg-[var(--surface)] hover:border-[var(--brand)]/40 hover:bg-[var(--brand)]/5'
          }`}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files) }}
        >
          <span className="text-lg mb-1">{dragging ? '📂' : '📎'}</span>
          <span className="text-xs text-[var(--text-2)]">{dragging ? S.dropActive : S.dropIdle}</span>
          <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden"
            onChange={e => { addFiles(e.target.files); e.target.value = '' }} />
        </label>
      )}
      {files.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {files.map(f => (
            <div key={f.name} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--surface-2)] border border-[var(--border)]">
              <span className="text-xs">📄</span>
              <span className="flex-1 text-xs text-[var(--text-1)] truncate">{f.name}</span>
              {onSetPageRanges && (
                <PageRangeBadge name={f.name} pageRanges={pageRanges} onClick={() => onSetPageRanges(f)} />
              )}
              <span className="text-xs text-[var(--text-3)] flex-shrink-0">{fmtSize(f.size)}</span>
              <button type="button" onClick={() => setFiles(prev => prev.filter(p => p.name !== f.name))}
                className="w-5 h-5 flex items-center justify-center text-red-400 transition text-xs flex-shrink-0">✗</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const MONTHS   = ['January','February','March','April','May','June','July','August','September','October','November','December']
const CUR_YEAR = new Date().getFullYear()
const YEARS    = Array.from({ length: 21 }, (_, i) => CUR_YEAR + i)

function HeaderSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)', borderRadius: '8px', padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minWidth: '80px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
        {value} <span style={{ fontSize: '0.6rem', color: 'var(--text-3)' }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 99999, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', maxHeight: '160px', overflowY: 'auto', minWidth: '100%' }}>
          {options.map(opt => (
            <div key={opt} onClick={() => { onChange(opt); setOpen(false) }}
              style={{ padding: '6px 12px', fontSize: '0.75rem', cursor: 'pointer', color: opt === value ? 'var(--brand)' : 'var(--text-1)', fontWeight: opt === value ? 700 : 400, background: opt === value ? 'rgba(14,165,233,0.08)' : 'transparent' }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = 'var(--surface)' }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = 'transparent' }}>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function DatePickerHeader({ date, changeMonth, changeYear, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) {
  const arrowBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', fontSize: '1rem', padding: '2px 6px', borderRadius: '6px' }
  return (
    <div className="flex items-center justify-between px-2 pb-2">
      <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} style={{ ...arrowBtn, opacity: prevMonthButtonDisabled ? 0.3 : 1 }}>‹</button>
      <div className="flex items-center gap-1.5">
        <HeaderSelect value={MONTHS[date.getMonth()]} options={MONTHS} onChange={m => changeMonth(MONTHS.indexOf(m))} />
        <HeaderSelect value={date.getFullYear()} options={YEARS} onChange={y => changeYear(y)} />
      </div>
      <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled} style={{ ...arrowBtn, opacity: nextMonthButtonDisabled ? 0.3 : 1 }}>›</button>
    </div>
  )
}

export function Counter({ value, onChange, min = 1, max = 200, step = 1 }) {
  const [raw, setRaw] = useState(String(value))
  // Only fields with a fractional step (e.g. the practice multiplier's 0.5) accept
  // a decimal point — every other Counter in the app keeps whole-number-only typing.
  const allowDecimal = step % 1 !== 0
  const pattern = allowDecimal ? /^\d*\.?\d*$/ : /^\d+$/
  const parse   = allowDecimal ? parseFloat : (v => parseInt(v, 10))

  useEffect(() => { setRaw(String(value)) }, [value])

  function roundToStep(n) {
    return step >= 1 ? Math.round(n) : Math.round(n / step) * step
  }

  function handleChange(e) {
    const v = e.target.value
    if (v === '' || pattern.test(v)) {
      const n = parse(v)
      // If the typed value is already over max (or under min, e.g. typing 0 when
      // 1 is the floor), clamp the displayed text too — otherwise, once `value`
      // is already at that bound, onChange is a no-op (same primitive, no
      // re-render) and the input keeps showing the unclamped digits.
      if (!isNaN(n) && n > max) { setRaw(String(max)); onChange(max); return }
      if (!isNaN(n) && v !== '' && n < min) { setRaw(String(min)); onChange(min); return }
      setRaw(v)
      if (!isNaN(n) && n >= min) onChange(n)
    }
  }

  function commit() {
    const n = parse(raw)
    if (isNaN(n) || raw === '') { setRaw(String(value)); return }
    onChange(roundToStep(Math.min(max, Math.max(min, n))))
  }

  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => onChange(roundToStep(Math.max(min, value - step)))}
        className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold transition-all"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
      >−</button>
      <input
        type="text"
        inputMode={allowDecimal ? 'decimal' : 'numeric'}
        value={raw}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={e => e.key === 'Enter' && commit()}
        className="w-10 text-center text-xs font-bold tabular-nums rounded-md focus:outline-none focus:border-[var(--brand)] transition"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)', padding: '2px 0' }}
      />
      <button type="button" onClick={() => onChange(roundToStep(Math.min(max, value + step)))}
        className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold transition-all"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
      >+</button>
    </div>
  )
}

const MAX_TOTAL = 100

export function QuestionSettingsPanel({ settings, difficulty, onDifficultyChange, onChange, types = Q_TYPES, lockedKeys = [], onDisallowedClick }) {
  function update(key, field, value) {
    onChange({ ...settings, [key]: { ...settings[key], [field]: value } })
  }
  function handleToggleClick(key, s, locked) {
    if (locked) { onDisallowedClick?.(key); return }
    update(key, 'enabled', !s.enabled)
  }
  const enabledTypes = types.filter(t => settings[t.key]?.enabled)
  const totalQ    = enabledTypes.reduce((s, t) => s + (settings[t.key]?.count ?? 0), 0)
  const totalMins = enabledTypes.reduce((s, t) => s + (settings[t.key]?.count ?? 0) * (settings[t.key]?.timePerQ ?? 0), 0)
  const remaining = MAX_TOTAL - totalQ

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-medium mb-2 text-[var(--text-3)]">Difficulty</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {DIFFICULTIES.map(({ key, label }) => {
            const active = difficulty === key
            return (
              <label key={key} className="flex items-center gap-1.5 cursor-pointer select-none">
                <div className="relative w-3.5 h-3.5 flex-shrink-0">
                  <input type="radio" name="difficulty" value={key} checked={active}
                    onChange={() => onDifficultyChange(key)} className="sr-only" />
                  <div className="w-3.5 h-3.5 rounded-full border-2 transition-all flex items-center justify-center"
                    style={{ borderColor: active ? 'var(--brand)' : 'var(--border)', background: 'var(--surface)' }}>
                    {active && <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--brand)' }} />}
                  </div>
                </div>
                <span className="text-xs font-medium transition-colors"
                  style={{ color: active ? 'var(--text-1)' : 'var(--text-3)' }}>{label}</span>
              </label>
            )
          })}
        </div>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
        <div className="hidden sm:grid grid-cols-[1fr_120px_160px] px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-wider"
             style={{ background: 'var(--surface-2)', color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
          <span>{S.questionTypeColLabel}</span>
          <span className="text-center whitespace-nowrap">Questions</span>
          <span className="text-center whitespace-nowrap">Time / Question (min)</span>
        </div>
        {types.map(({ key, label, desc }, i) => {
          const s       = settings[key] ?? { enabled: true, count: 10, timePerQ: 1 }
          const locked  = lockedKeys.includes(key)
          const others  = enabledTypes.filter(t => t.key !== key).reduce((sum, t) => sum + (settings[t.key]?.count ?? 0), 0)
          const dynMax  = Math.max(1, MAX_TOTAL - others)
          return (
            <div key={key} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_160px] items-start sm:items-center gap-3 sm:gap-0 px-4 py-3 transition-colors"
                 style={{ borderBottom: i < types.length - 1 ? '1px solid var(--border)' : 'none', opacity: locked ? 0.6 : (s.enabled ? 1 : 0.45) }}>
              <div className="flex items-center gap-2.5">
                <button type="button" onClick={() => handleToggleClick(key, s, locked)}
                  className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all"
                  style={{ background: s.enabled ? 'var(--brand)' : 'var(--surface-2)', border: `1.5px solid ${s.enabled ? 'var(--brand)' : 'var(--border)'}` }}>
                  {s.enabled && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4l2.5 2.5L9 1"/></svg>}
                </button>
                <div className={locked ? 'cursor-pointer' : ''} onClick={locked ? () => handleToggleClick(key, s, locked) : undefined}>
                  <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: 'var(--text-1)' }}>
                    {label}
                    {locked && <span className="text-[0.6rem] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>{S.lockedBadge}</span>}
                  </p>
                  <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>{desc}</p>
                </div>
              </div>
              <div className="flex sm:contents gap-4 pl-6 sm:pl-0" style={locked ? { pointerEvents: 'none' } : undefined}>
                <div className="flex flex-col items-center gap-1 sm:flex sm:justify-center">
                  <span className="text-[0.6rem] uppercase tracking-wider sm:hidden" style={{ color: 'var(--text-3)' }}>Questions</span>
                  <Counter value={s.count} onChange={v => update(key, 'count', v)} min={1} max={dynMax} step={1} />
                </div>
                <div className="flex flex-col items-center gap-1 sm:flex sm:justify-center">
                  <span className="text-[0.6rem] uppercase tracking-wider text-center sm:hidden" style={{ color: 'var(--text-3)' }}>Time / Question</span>
                  <Counter value={s.timePerQ} onChange={v => update(key, 'timePerQ', v)} min={0.5} max={30} step={0.5} />
                </div>
              </div>
            </div>
          )
        })}
        <div className="px-4 py-2.5" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
          <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-1">
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>
              Total <span className="font-bold" style={{ color: totalQ > MAX_TOTAL ? '#f87171' : 'var(--text-1)' }}>{totalQ}</span>
              <span style={{ color: 'var(--text-3)' }}> / {MAX_TOTAL}</span>
              {remaining > 0 && <span style={{ color: 'var(--text-3)' }}> ({remaining} remaining)</span>}
            </span>
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>
              Duration <span className="font-bold" style={{ color: 'var(--brand)' }}>{fmtDuration(totalMins)}</span>
            </span>
          </div>
          {totalQ > MAX_TOTAL && (
            <p className="text-xs mt-1" style={{ color: '#f87171' }}>
              Total exceeds {MAX_TOTAL} questions. Reduce the count to proceed.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Bloom's Taxonomy distribution ────────────────────────────────────────────

export const BLOOM_LEVELS = [
  { key: 'remember',   label: 'Remember',   pct: 0.25  },
  { key: 'understand', label: 'Understand', pct: 0.25  },
  { key: 'apply',      label: 'Apply',      pct: 0.125 },
  { key: 'analyze',    label: 'Analyze',    pct: 0.125 },
  { key: 'evaluate',   label: 'Evaluate',   pct: 0.125 },
  { key: 'create',     label: 'Create',     pct: 0.125 },
]

export const DEFAULT_BLOOM_DISTRIBUTION = { remember: 0, understand: 0, apply: 0, analyze: 0, evaluate: 0, create: 0 }

// Default marks-per-level — editable by the examiner, not sent to the AI during
// generation. Only read back at evaluation time.
export const DEFAULT_BLOOM_MARKS = { remember: 1, understand: 2, apply: 3, analyze: 4, evaluate: 5, create: 6 }

// Fixed 50/25/25 split (Remember+Understand / Apply+Analyze / Evaluate+Create), rounded
// to whole questions via largest-remainder so the counts always sum exactly to total.
// `saCount` caps `create` (only short-answer questions can be Create-level) — any excess
// is pushed onto Remember so the total is unaffected.
export function autoBloomSplit(total, saCount = Infinity) {
  const raw     = BLOOM_LEVELS.map(l => l.pct * total)
  const floored = raw.map(Math.floor)
  const remainder = total - floored.reduce((a, b) => a + b, 0)
  const byFrac = raw.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac)
  const counts = [...floored]
  for (let k = 0; k < remainder; k++) counts[byFrac[k % byFrac.length].i]++
  const dist = {}
  BLOOM_LEVELS.forEach((l, i) => { dist[l.key] = counts[i] })

  const cap = Math.max(0, Math.floor(saCount))
  if (dist.create > cap) {
    dist.remember += dist.create - cap
    dist.create = cap
  }
  return dist
}

export function BloomDistributionPanel({ enabled, onToggle, distribution, onChange, onReset, marks, onMarksChange, questionSettings, allowed = true, onDisallowedClick }) {
  const totalQ  = Q_TYPES.filter(t => questionSettings[t.key]?.enabled).reduce((s, t) => s + (questionSettings[t.key]?.count ?? 0), 0)
  const saCount = questionSettings.short_answer?.enabled ? (questionSettings.short_answer?.count ?? 0) : 0
  const sum     = BLOOM_LEVELS.reduce((s, l) => s + (distribution[l.key] ?? 0), 0)
  const mismatch      = sum !== totalQ
  const createOverflow = (distribution.create ?? 0) > saCount

  function update(key, value) {
    onChange({ ...distribution, [key]: value })
  }

  function updateMarks(key, value) {
    onMarksChange({ ...marks, [key]: value })
  }

  function handleToggleClick() {
    if (!allowed) { onDisallowedClick?.(); return }
    onToggle(!enabled)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        <button type="button" onClick={handleToggleClick}
          className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all"
          style={{ background: enabled ? 'var(--brand)' : 'var(--surface-2)', border: `1.5px solid ${enabled ? 'var(--brand)' : 'var(--border)'}`, opacity: allowed ? 1 : 0.6 }}>
          {enabled && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4l2.5 2.5L9 1"/></svg>}
        </button>
        <div className="flex-1 cursor-pointer" onClick={!allowed ? handleToggleClick : undefined}>
          <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: 'var(--text-1)' }}>
            {S.bloomDistributionLabel}
            {!allowed && <span className="text-[0.6rem] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>{S.lockedBadge}</span>}
          </p>
          <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>Tag exam-pool questions by cognitive level (optional)</p>
        </div>
      </div>

      {enabled && (
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          <div className="hidden sm:grid grid-cols-[1fr_120px_120px] px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-wider"
               style={{ background: 'var(--surface-2)', color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
            <span>Level</span>
            <span className="text-center">Questions</span>
            <span className="text-center">Marks</span>
          </div>
          {BLOOM_LEVELS.map(({ key, label }, i) => (
            <div key={key} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px] items-start sm:items-center gap-2 sm:gap-0 px-4 py-3"
                 style={{ borderBottom: i < BLOOM_LEVELS.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>{label}</p>
              <div className="flex sm:contents gap-4">
                <div className="flex flex-col items-center gap-1 sm:flex sm:justify-center">
                  <span className="text-[0.6rem] uppercase tracking-wider sm:hidden" style={{ color: 'var(--text-3)' }}>Questions</span>
                  <Counter value={distribution[key] ?? 0} onChange={v => update(key, v)} min={0} max={totalQ} step={1} />
                </div>
                <div className="flex flex-col items-center gap-1 sm:flex sm:justify-center">
                  <span className="text-[0.6rem] uppercase tracking-wider sm:hidden" style={{ color: 'var(--text-3)' }}>Marks</span>
                  <Counter value={marks[key] ?? 1} onChange={v => updateMarks(key, v)} min={1} max={20} step={1} />
                </div>
              </div>
            </div>
          ))}
          <div className="px-4 py-2.5 space-y-1" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-1">
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                Total <span className="font-bold" style={{ color: mismatch ? '#f87171' : 'var(--text-1)' }}>{sum}</span>
                <span style={{ color: 'var(--text-3)' }}> / {totalQ}</span>
              </span>
              <button type="button" onClick={onReset}
                className="text-[0.65rem] font-medium underline" style={{ color: 'var(--brand)' }}>
                Reset to Default
              </button>
            </div>
            {mismatch && (
              <p className="text-xs" style={{ color: '#f87171' }}>
                Your question types add up to {totalQ}, but the Bloom's Taxonomy levels add up to {sum}. These two totals need to match — adjust either one.
              </p>
            )}
            {createOverflow && (
              <p className="text-xs" style={{ color: '#f87171' }}>
                You've set {distribution.create} Create-level {distribution.create === 1 ? 'question' : 'questions'}, but only {saCount} short-answer {saCount === 1 ? 'question is' : 'questions are'} available. Only short-answer questions can be Create-level — reduce Create or add more short-answer questions.
              </p>
            )}
          </div>
      </div>
      )}
    </div>
  )
}

export function TrialsSettingsPanel({ trialsEnabled, onTrialsEnabledChange, trialLevelLockEnabled, onTrialLevelLockChange }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        <button type="button" onClick={() => onTrialsEnabledChange(!trialsEnabled)}
          className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all"
          style={{ background: trialsEnabled ? 'var(--brand)' : 'var(--surface-2)', border: `1.5px solid ${trialsEnabled ? 'var(--brand)' : 'var(--border)'}` }}>
          {trialsEnabled && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4l2.5 2.5L9 1"/></svg>}
        </button>
        <div className="flex-1 cursor-pointer" onClick={() => onTrialsEnabledChange(!trialsEnabled)}>
          <p className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>{S.trialsEnabledLabel}</p>
          <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>{S.trialsEnabledDesc}</p>
        </div>
      </div>

      {trialsEnabled && (
        <div className="flex items-center gap-2.5 pl-1">
          {/* Inverted display on purpose — the field underneath is still
              trialLevelLockEnabled, but the UI presents it as the positive
              "allow skipping" choice: checked = lock disabled. */}
          <button type="button" onClick={() => onTrialLevelLockChange(!trialLevelLockEnabled)}
            className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all"
            style={{ background: !trialLevelLockEnabled ? 'var(--brand)' : 'var(--surface-2)', border: `1.5px solid ${!trialLevelLockEnabled ? 'var(--brand)' : 'var(--border)'}` }}>
            {!trialLevelLockEnabled && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4l2.5 2.5L9 1"/></svg>}
          </button>
          <div className="flex-1 cursor-pointer" onClick={() => onTrialLevelLockChange(!trialLevelLockEnabled)}>
            <p className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>{S.trialLevelLockLabel}</p>
            <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>{S.trialLevelLockDesc}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export function QuestionTypeIndicatorToggle({ value, onChange }) {
  return (
    <div className="flex items-center gap-2.5">
      <button type="button" onClick={() => onChange(!value)}
        className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all"
        style={{ background: value ? 'var(--brand)' : 'var(--surface-2)', border: `1.5px solid ${value ? 'var(--brand)' : 'var(--border)'}` }}>
        {value && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4l2.5 2.5L9 1"/></svg>}
      </button>
      <div className="flex-1 cursor-pointer" onClick={() => onChange(!value)}>
        <p className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>{S.showQuestionTypeLabel}</p>
        <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>{S.showQuestionTypeDesc}</p>
      </div>
    </div>
  )
}

export const DEFAULT_PRACTICE_MULTIPLIER = 3

export function PracticeMultiplierPanel({ value, onChange, totalQ }) {
  const maxMult = maxPracticeMultiplier(totalQ)
  // Keep the stored value in range as totalQ changes elsewhere (e.g. question counts
  // edited after this multiplier was set) — same auto-clamp pattern as Bloom's sync.
  useEffect(() => { if (value > maxMult) onChange(maxMult) }, [maxMult])

  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>{S.practiceMultiplierLabel}</p>
        <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>{S.practiceMultiplierDesc}</p>
        <p className="text-[0.65rem] mt-0.5" style={{ color: 'var(--text-3)' }}>
          Generates up to {Math.round(value * totalQ)} questions per difficulty level <span style={{ color: 'var(--text-3)' }}>(limit {MAX_PRACTICE_QUESTIONS})</span>
        </p>
      </div>
      <Counter value={value} onChange={onChange} min={1} max={maxMult} step={PRACTICE_MULTIPLIER_STEP} />
    </div>
  )
}

// ── Locked feature dialog ────────────────────────────────────────────────────

const SUPPORT_WHATSAPP = '918089544500'
const SUPPORT_EMAIL    = 'support@radtar.com'

// `feature` is { key, label } — key looks up the specific price for this
// feature in featureUnlockPrices, label is what's shown to the user.
export function LockedFeatureDialog({ feature, orgName, price, onBuyCredits, onClose }) {
  const label = feature?.label ?? feature
  const text = `Hi, I'd like to request access to ${label} on RADTAR SkillAssess for ${orgName || 'my organisation'}.`

  // Disabled — self-service Buy Credits replaced the contact-support flow as
  // the primary path. Left here (not deleted) in case it's needed again.
  // function contactWhatsApp() {
  //   window.open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer')
  //   onClose()
  // }
  // function contactEmail() {
  //   window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`${label} access request — ${orgName || ''}`)}&body=${encodeURIComponent(text)}`
  //   onClose()
  // }
  function buyCredits() {
    onBuyCredits()
    onClose()
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4"
         style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)' }}
         onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl border p-6 animate-fade-up"
           style={{ background: 'var(--surface)', borderColor: 'var(--border)', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}
           onClick={e => e.stopPropagation()}>
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl mb-4" style={{ background: 'var(--surface-2)' }}>🔒</div>
          <h3 className="text-base font-bold mb-1.5" style={{ color: 'var(--text-1)' }}>{label} is locked</h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
            Your organisation doesn't have access to {label} yet. It unlocks automatically once your credits balance reaches ₹{(price ?? 0).toLocaleString('en-IN')}.
          </p>
        </div>
        {/*
        <div className="flex items-center justify-center gap-5 mb-4">
          <button onClick={contactWhatsApp} title="Contact on WhatsApp" className="flex flex-col items-center gap-1.5">
            <span className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
              style={{ background: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.3)', color: '#25d366' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.828L.057 23.405a.75.75 0 00.918.918l5.578-1.466A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.932 0-3.733-.505-5.288-1.388l-.38-.219-3.932 1.034 1.034-3.932-.219-.38A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            </span>
            <span className="text-[0.65rem] font-medium" style={{ color: 'var(--text-3)' }}>WhatsApp</span>
          </button>
          <button onClick={contactEmail} title="Contact via Email" className="flex flex-col items-center gap-1.5">
            <span className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
              style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            </span>
            <span className="text-[0.65rem] font-medium" style={{ color: 'var(--text-3)' }}>Email</span>
          </button>
        </div>
        */}
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="btn-ghost flex-1 justify-center text-sm">Cancel</button>
          {onBuyCredits && (
            <button onClick={buyCredits} className="btn-primary flex-1 justify-center text-sm">Buy Credits</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function CreateExamForm({ onSubmit, onCancel, saving, showToast }) {
  const { theme } = useTheme()
  const { user, updateUser } = useAuth()
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false)
  const { featureUnlockPrices, estimatedCostPerMB } = usePlatformPublicSettings()
  const { lockedQTypeKeys, bloomsAllowed } = resolveExamAccess(user, featureUnlockPrices)

  const [name,          setName]          = useState('')
  const [desc,          setDesc]          = useState('')
  const [textbookFiles, setTextbookFiles] = useState([])
  const [patternFiles,  setPatternFiles]  = useState([])
  const [pageRanges,    setPageRanges]    = useState({}) // { [filename]: [[start,end], ...] }
  const [pageRangeFile, setPageRangeFile] = useState(null) // File currently open in the picker
  const [dragT,         setDragT]         = useState(false)
  const [dragP,         setDragP]         = useState(false)
  const [qSettings,     setQSettings]     = useState(() => {
    const filtered = { ...DEFAULT_Q_SETTINGS }
    for (const t of Q_TYPES) {
      if (lockedQTypeKeys.includes(t.key)) filtered[t.key] = { ...filtered[t.key], enabled: false }
    }
    return filtered
  })
  const [difficulty,    setDifficulty]    = useState('Intermediate')
  const [lockedFeatureClicked, setLockedFeatureClicked] = useState(null) // e.g. "MSQ" or "Bloom's Taxonomy"
  const [bloomEnabled,      setBloomEnabled]      = useState(false)
  const [bloomDistribution, setBloomDistribution] = useState(() => autoBloomSplit(totalQFor(DEFAULT_Q_SETTINGS), saCountFor(DEFAULT_Q_SETTINGS)))
  const [bloomTouched,      setBloomTouched]       = useState(false)
  const [bloomMarks,        setBloomMarks]        = useState(DEFAULT_BLOOM_MARKS)
  const [practiceMultiplier, setPracticeMultiplier] = useState(DEFAULT_PRACTICE_MULTIPLIER)
  const [trialsEnabled,         setTrialsEnabled]         = useState(true)
  // Default to locked (Allow Skip Trials off) — sequential level unlock required
  // unless the examiner explicitly turns skipping on.
  const [trialLevelLockEnabled, setTrialLevelLockEnabled] = useState(true)
  const [showQuestionTypeIndicator, setShowQuestionTypeIndicator] = useState(false)
  const [validFrom,     setValidFrom]     = useState(new Date())
  const [validTill,     setValidTill]     = useState(new Date())
  const [costCheck,     setCostCheck]     = useState(null) // { blocked, message } once the uploaded PDFs' size has been checked against the balance
  const refT = useRef(null)
  const refP = useRef(null)

  // Same size-vs-balance check startGeneration runs server-side, moved up to
  // upload time — the examiner already has the files in the browser, so this
  // avoids a wasted create+upload round-trip only to find out generation
  // would be refused for insufficient credits.
  const totalPdfBytes = textbookFiles.reduce((s, f) => s + f.size, 0) + patternFiles.reduce((s, f) => s + f.size, 0)
  useEffect(() => {
    if (totalPdfBytes === 0) { setCostCheck(null); return }
    const timer = setTimeout(() => {
      api.post('/api/exams/estimate-cost', { totalSizeBytes: totalPdfBytes })
        .then(setCostCheck)
        .catch(() => setCostCheck(null))
      // Re-runs after a Buy Credits purchase too (user.creditsBalance changes),
      // so the warning clears the moment the balance actually covers it.
    }, 500)
    return () => clearTimeout(timer)
  }, [totalPdfBytes, user?.creditsBalance])

  // Keep the distribution synced to the auto 50/25/25 split as question counts change,
  // until the examiner edits a Bloom counter directly. Only matters while enabled.
  useEffect(() => {
    if (bloomEnabled && !bloomTouched) setBloomDistribution(autoBloomSplit(totalQFor(qSettings), saCountFor(qSettings)))
  }, [qSettings, bloomEnabled, bloomTouched])

  function handleBloomToggle(next) {
    setBloomEnabled(next)
    if (next && !bloomTouched) setBloomDistribution(autoBloomSplit(totalQFor(qSettings), saCountFor(qSettings)))
  }

  function handleBloomChange(next) {
    setBloomTouched(true)
    setBloomDistribution(next)
  }

  function handleBloomReset() {
    setBloomTouched(false)
    setBloomDistribution(autoBloomSplit(totalQFor(qSettings), saCountFor(qSettings)))
  }

  const bloomSum   = BLOOM_LEVELS.reduce((s, l) => s + (bloomDistribution[l.key] ?? 0), 0)
  const bloomValid = !bloomEnabled || (bloomSum === totalQFor(qSettings) && (bloomDistribution.create ?? 0) <= saCountFor(qSettings))

  function handleSubmit(e) {
    e.preventDefault()
    const hasEnabled = Q_TYPES.some(t => qSettings[t.key]?.enabled)
    if (!name.trim() || textbookFiles.length === 0 || !hasEnabled || !validFrom || !validTill || !bloomValid) return
    if (costCheck?.blocked) { setBuyCreditsOpen(true); return }
    const tillEod = new Date(validTill); tillEod.setHours(23, 59, 59, 999)
    // Safety net — force any disallowed type off even if state somehow got out of sync
    const safeQSettings = Object.fromEntries(
      Object.entries(qSettings).map(([key, s]) => [key, lockedQTypeKeys.includes(key) ? { ...s, enabled: false } : s])
    )
    onSubmit({
      name, description: desc, textbookFiles, patternFiles, questionSettings: safeQSettings, difficulty,
      bloomEnabled: bloomsAllowed && bloomEnabled,
      bloomDistribution: (bloomsAllowed && bloomEnabled) ? bloomDistribution : undefined,
      bloomMarks: (bloomsAllowed && bloomEnabled) ? bloomMarks : undefined,
      practiceMultiplier,
      trialsEnabled, trialLevelLockEnabled: trialsEnabled && trialLevelLockEnabled,
      showQuestionTypeIndicator,
      pageRanges: cleanPageRanges(pageRanges),
      validFrom: validFrom.toISOString(), validTill: tillEod.toISOString(),
    })
  }

  const hasFiles    = textbookFiles.length > 0
  const hasEnabled  = Q_TYPES.some(t => qSettings[t.key]?.enabled)
  const totalQ      = totalQFor(qSettings)
  const overLimit   = totalQ > MAX_EXAM_QUESTIONS

  return (
    <form onSubmit={handleSubmit} className="card p-5 mb-6 animate-fade-up" style={{ borderColor: 'var(--brand)', borderWidth: '1px' }}>
      <h3 className="text-sm font-bold text-[var(--text-1)] mb-4">{S.createTitle}</h3>

      <div className="space-y-3 mb-5">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-[var(--text-3)]">{S.examNameLabel}</label>
            <CharCount value={name} max={NAME_MAX} />
          </div>
          <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder={S.examNamePlaceholder} maxLength={NAME_MAX}
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:border-[var(--brand)] transition" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <p className="text-xs font-medium text-[var(--text-3)]">{S.textbookPdfsLabel}</p>
            <div className="relative group">
              <span className="w-3.5 h-3.5 rounded-full border flex items-center justify-center text-[0.6rem] font-bold cursor-default flex-shrink-0"
                    style={{ borderColor: 'var(--text-3)', color: 'var(--text-3)' }}>i</span>
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 text-[0.7rem] leading-relaxed px-3 py-2 rounded-lg shadow-xl z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                   style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                {S.textbookPdfsInfo}
              </div>
            </div>
          </div>
          <p className="text-[0.65rem] text-[var(--text-3)] mb-2">{S.textbookPdfsDesc}</p>
          <FileDropZone files={textbookFiles} setFiles={setTextbookFiles} dragging={dragT} setDragging={setDragT} fileRef={refT} maxMB={15}
            pageRanges={pageRanges} onSetPageRanges={setPageRangeFile} />
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <p className="text-xs font-medium text-[var(--text-3)]">{S.patternPdfsLabel}</p>
            <div className="relative group">
              <span className="w-3.5 h-3.5 rounded-full border flex items-center justify-center text-[0.6rem] font-bold cursor-default flex-shrink-0"
                    style={{ borderColor: 'var(--text-3)', color: 'var(--text-3)' }}>i</span>
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 text-[0.7rem] leading-relaxed px-3 py-2 rounded-lg shadow-xl z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                   style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                {S.patternPdfsInfo}
              </div>
            </div>
          </div>
          <p className="text-[0.65rem] text-[var(--text-3)] mb-2">{S.patternPdfsDesc}</p>
          <FileDropZone files={patternFiles} setFiles={setPatternFiles} dragging={dragP} setDragging={setDragP} fileRef={refP} maxMB={5} />
        </div>
      </div>

      {estimatedCostPerMB != null && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-xl mb-5 text-[0.7rem] sm:text-xs leading-relaxed"
             style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
          <span className="flex-shrink-0">💡</span>
          <span>Generating questions costs approximately <strong style={{ color: 'var(--text-2)' }}>₹{estimatedCostPerMB}</strong> per MB of uploaded PDF.</span>
        </div>
      )}

      {costCheck?.blocked && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-xl mb-5 text-xs leading-relaxed"
             style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', color: '#f87171' }}>
          <span className="flex-1">⚠ {costCheck.message}</span>
          <button type="button" onClick={() => setBuyCreditsOpen(true)}
            className="btn-primary text-xs px-3 py-1.5 flex-shrink-0 justify-center">Buy Credits</button>
        </div>
      )}

      <div className="mb-5">
        <QuestionSettingsPanel settings={qSettings} onChange={setQSettings} difficulty={difficulty} onDifficultyChange={setDifficulty}
          lockedKeys={lockedQTypeKeys} onDisallowedClick={key => setLockedFeatureClicked({ key, label: Q_TYPES.find(t => t.key === key)?.label ?? key })} />
      </div>

      <p className="text-[0.68rem] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-3)' }}>Configurations</p>

      <div className="mb-5">
        <BloomDistributionPanel enabled={bloomEnabled} onToggle={handleBloomToggle} distribution={bloomDistribution} onChange={handleBloomChange} onReset={handleBloomReset}
          marks={bloomMarks} onMarksChange={setBloomMarks} questionSettings={qSettings}
          allowed={bloomsAllowed} onDisallowedClick={() => setLockedFeatureClicked({ key: 'bloomsTaxonomy', label: "Bloom's Taxonomy" })} />
      </div>

      <div className="mb-5">
        <QuestionTypeIndicatorToggle value={showQuestionTypeIndicator} onChange={setShowQuestionTypeIndicator} />
      </div>

      <div className="mb-5">
        <TrialsSettingsPanel
          trialsEnabled={trialsEnabled} onTrialsEnabledChange={setTrialsEnabled}
          trialLevelLockEnabled={trialLevelLockEnabled} onTrialLevelLockChange={setTrialLevelLockEnabled}
        />
      </div>

      {trialsEnabled && (
        <div className="mb-5">
          <PracticeMultiplierPanel value={practiceMultiplier} onChange={setPracticeMultiplier} totalQ={totalQ} />
        </div>
      )}

      {lockedFeatureClicked && (
        <LockedFeatureDialog feature={lockedFeatureClicked} orgName={user?.name} price={featureUnlockPrices?.[lockedFeatureClicked.key] ?? 0}
          onBuyCredits={() => setBuyCreditsOpen(true)} onClose={() => setLockedFeatureClicked(null)} />
      )}
      {buyCreditsOpen && (
        <BuyCreditsModal user={user} updateUser={updateUser} showToast={showToast} onClose={() => setBuyCreditsOpen(false)} />
      )}

      {pageRangeFile && (
        <PageRangePicker
          fileName={pageRangeFile.name}
          source={{ file: pageRangeFile }}
          initialRanges={pageRanges[pageRangeFile.name]}
          onDone={ranges => { setPageRanges(prev => ({ ...prev, [pageRangeFile.name]: ranges })); setPageRangeFile(null) }}
          onClose={() => setPageRangeFile(null)}
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="text-xs font-medium text-[var(--text-3)] mb-1.5 block">Valid From <span style={{ color: 'var(--text-3)' }}>*</span></label>
          <DatePicker
            selected={validFrom}
            onChange={date => { setValidFrom(date); if (validTill && date > validTill) setValidTill(null) }}
            minDate={new Date()}
            dateFormat="dd/MM/yyyy"
            placeholderText="dd/mm/yyyy"
            renderCustomHeader={props => <DatePickerHeader {...props} />}
            className="w-full px-3 py-2.5 rounded-xl text-sm border transition focus:outline-none cursor-pointer"
            wrapperClassName="w-full"
            calendarClassName={theme === 'dark' ? 'dp-dark' : ''}
            portalId="dp-portal"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-[var(--text-3)] mb-1.5 block">Valid Till <span style={{ color: 'var(--text-3)' }}>*</span></label>
          <DatePicker
            selected={validTill}
            onChange={date => setValidTill(date)}
            minDate={validFrom ?? new Date()}
            dateFormat="dd/MM/yyyy"
            placeholderText="dd/mm/yyyy"
            renderCustomHeader={props => <DatePickerHeader {...props} />}
            className="w-full px-3 py-2.5 rounded-xl text-sm border transition focus:outline-none cursor-pointer"
            wrapperClassName="w-full"
            calendarClassName={theme === 'dark' ? 'dp-dark' : ''}
            portalId="dp-portal"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="btn-ghost text-xs px-4 py-2">{S.cancel}</button>
        <button type="submit" disabled={!name.trim() || !hasFiles || !hasEnabled || !validFrom || !validTill || overLimit || !bloomValid || saving || costCheck?.blocked} className="btn-primary text-xs px-4 py-2 disabled:opacity-50">
          {saving
            ? <><span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" /> {S.creating}</>
            : S.createBtn}
        </button>
      </div>
    </form>
  )
}
